// Slash-command registry for the chat composer.
//
// A slash command is typed at the START of an empty-ish composer
// (`/name [args]`) and transforms the outgoing message text at send
// time. This module is the pure core: the command list + the parse /
// match / run helpers. The composer owns the picker UI + wiring.
//
// The built-in set is intentionally limited to PURELY CLIENT-SIDE text
// commands — the Element "/shrug" family — which just rewrite the
// message body and need no host, protocol, or message-type support.
// Action commands that DO need host support (`/me` emote, `/plain`
// raw-send, `/invite`, …) are out of scope here; hosts can add their own
// via the composer's `slashCommands` prop (each command's `run` may
// return the rewritten body, or `null` to signal "handled, send
// nothing").

export interface SlashCommand {
  /** Command name WITHOUT the leading slash, lowercase (e.g. `shrug`). */
  readonly name: string;
  /** One-line description shown in the picker. */
  readonly description: string;
  /** Optional argument hint shown after the name (e.g. `<message>`). */
  readonly argsHint?: string;
  /**
   * Transform the message. `args` is everything typed after the command
   * name (may be empty). Return the rewritten message body to send, or
   * `null` to send nothing (the command fully handled itself).
   */
  readonly run: (args: string) => string | null;
}

/** Append the shrug/emote glyph to the (optional) message text. */
function withSuffix(glyph: string): (args: string) => string {
  return (args: string) => {
    const trimmed = args.trim();
    return trimmed.length > 0 ? `${trimmed} ${glyph}` : glyph;
  };
}

/**
 * Built-in, host-independent commands. Mirrors the Element/Matrix
 * composer's text-emoticon slash commands.
 */
export const BUILTIN_SLASH_COMMANDS: readonly SlashCommand[] = [
  {
    name: "shrug",
    description: "Prepends ¯\\_(ツ)_/¯ to a plain-text message",
    argsHint: "<message>",
    run: withSuffix("¯\\_(ツ)_/¯"),
  },
  {
    name: "tableflip",
    description: "Prepends (╯°□°)╯︵ ┻━┻ to a plain-text message",
    argsHint: "<message>",
    run: withSuffix("(╯°□°)╯︵ ┻━┻"),
  },
  {
    name: "unflip",
    description: "Prepends ┬─┬ ノ( ゜-゜ノ) to a plain-text message",
    argsHint: "<message>",
    run: withSuffix("┬─┬ ノ( ゜-゜ノ)"),
  },
  {
    name: "lenny",
    description: "Prepends ( ͡° ͜ʖ ͡°) to a plain-text message",
    argsHint: "<message>",
    run: withSuffix("( ͡° ͜ʖ ͡°)"),
  },
];

/**
 * The composer opens the slash picker only while the buffer is JUST the
 * command name being typed — a leading slash followed by word chars and
 * no whitespace yet (`/`, `/sh`, `/shrug`). Once the user types a space
 * the picker closes and the rest is treated as the command's args.
 * Returns the partial command name (may be empty for a bare `/`), or
 * `null` when the buffer isn't in "typing a command name" state.
 */
export function matchSlashQuery(text: string): string | null {
  const m = /^\/([a-zA-Z]*)$/.exec(text);
  return m ? (m[1] ?? "") : null;
}

/** Case-insensitive prefix filter over a command list. */
export function filterSlashCommands(
  commands: readonly SlashCommand[],
  query: string,
): readonly SlashCommand[] {
  const q = query.toLowerCase();
  if (q.length === 0) return commands;
  return commands.filter((c) => c.name.startsWith(q));
}

/**
 * If `text` is a full slash-command invocation (`/name` or `/name args`),
 * run it and return the rewritten body (or `null` when the command
 * chose to send nothing). Returns `undefined` when `text` is not a known
 * command, so the caller sends it verbatim.
 */
export function runSlashCommand(
  commands: readonly SlashCommand[],
  text: string,
): string | null | undefined {
  const m = /^\/([a-zA-Z]+)(?:\s+([\s\S]*))?$/.exec(text);
  if (!m) return undefined;
  const name = (m[1] ?? "").toLowerCase();
  const command = commands.find((c) => c.name === name);
  if (command === undefined) return undefined;
  return command.run(m[2] ?? "");
}
